import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  FileUpload as FileUploadIcon,
  QrCode2 as QrCode2Icon,
} from '@mui/icons-material';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { QRCodeSVG } from 'qrcode.react';
import { db, functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { WaiverKey, WaiverSignature, WaiverTemplate } from '../lib/db/types';

interface WaiverTemplateWithId extends WaiverTemplate {
  id: string;
}

interface WaiverSignatureWithId extends WaiverSignature {
  id: string;
}

interface LookupResult {
  emailLower: string;
  signedAt: string | null;
  signatureId: string;
  memberId?: string | null;
  leadId?: string | null;
}

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function formatTimestamp(value: any) {
  if (value?.toDate) {
    return value.toDate().toLocaleString();
  }
  return 'N/A';
}

function timestampMillis(value: any) {
  if (value?.toMillis) {
    return value.toMillis();
  }
  if (value?.toDate) {
    return value.toDate().getTime();
  }
  return 0;
}

function isIndexBuildingError(error: any) {
  const message = String(error?.message || '');
  return error?.code === 'failed-precondition' && message.includes('requires an index');
}

function defaultTitleFromFile(file: File | null) {
  if (!file) return '';
  return file.name.replace(/\.pdf$/i, '').trim();
}

function readFileBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const [, base64 = ''] = result.split(',');
      if (!base64) {
        reject(new Error('Could not read waiver file'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read waiver file'));
    reader.readAsDataURL(file);
  });
}

async function loadRecentWaiverSignatures(tenantId: string) {
  try {
    const signatureSnapshot = await getDocs(
      query(
        collection(db, 'waiverSignatures'),
        where('tenantId', '==', tenantId),
        orderBy('createdAt', 'desc'),
        limit(10)
      )
    );

    return signatureSnapshot.docs.map((document) => ({
      id: document.id,
      ...document.data(),
    })) as WaiverSignatureWithId[];
  } catch (error) {
    if (!isIndexBuildingError(error)) {
      throw error;
    }

    const fallbackSnapshot = await getDocs(
      query(
        collection(db, 'waiverSignatures'),
        where('tenantId', '==', tenantId),
        limit(50)
      )
    );

    return (fallbackSnapshot.docs.map((document) => ({
      id: document.id,
      ...document.data(),
    })) as WaiverSignatureWithId[])
      .sort((a, b) => timestampMillis(b.createdAt) - timestampMillis(a.createdAt))
      .slice(0, 10);
  }
}

export function WaiversPage() {
  const { tenantId, activeTenant, roles } = useTenant();
  const [activeTemplate, setActiveTemplate] = useState<WaiverTemplateWithId | null>(null);
  const [recentSignatures, setRecentSignatures] = useState<WaiverSignatureWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const canManageTemplates = roles.some((role) => role === 'owner' || role === 'manager');
  const publicWaiverUrl = useMemo(() => {
    if (!activeTenant?.slug || typeof window === 'undefined') {
      return '';
    }
    return `${window.location.origin}/waiver/${activeTenant.slug}`;
  }, [activeTenant?.slug]);

  const loadWaiverData = async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const [templateSnapshot, signatures] = await Promise.all([
        getDocs(
          query(
            collection(db, 'waiverTemplates'),
            where('tenantId', '==', tenantId),
            where('status', '==', 'active'),
            limit(1)
          )
        ),
        loadRecentWaiverSignatures(tenantId),
      ]);

      setActiveTemplate(
        templateSnapshot.empty
          ? null
          : ({ id: templateSnapshot.docs[0].id, ...templateSnapshot.docs[0].data() } as WaiverTemplateWithId)
      );
      setRecentSignatures(signatures);
    } catch (error: any) {
      console.error('Error loading waivers:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load waiver data' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWaiverData();
  }, [tenantId]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && !title) {
      setTitle(defaultTitleFromFile(file));
    }
  };

  const handleUpload = async () => {
    if (!tenantId || !selectedFile) return;

    if (selectedFile.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Waiver upload must be a PDF file' });
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Waiver file must be 5 MB or smaller' });
      return;
    }

    setUploading(true);
    setMessage(null);

    const templateId = doc(collection(db, 'waiverTemplates')).id;
    const storagePath = `${tenantId}/waivers/${templateId}.pdf`;

    try {
      const fileBase64 = await readFileBase64(selectedFile);

      const upload = httpsCallable(functions, 'uploadWaiverTemplate');
      await upload({
        tenantId,
        templateId,
        title: title.trim() || defaultTitleFromFile(selectedFile),
        originalFileName: selectedFile.name,
        storagePath,
        contentType: selectedFile.type,
        fileSize: selectedFile.size,
        fileBase64,
      });

      setSelectedFile(null);
      setTitle('');
      setMessage({ type: 'success', text: 'Waiver published successfully' });
      await loadWaiverData();
    } catch (error: any) {
      console.error('Error publishing waiver:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to publish waiver' });
    } finally {
      setUploading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicWaiverUrl) return;

    try {
      await navigator.clipboard.writeText(publicWaiverUrl);
      setMessage({ type: 'success', text: 'Public waiver link copied to clipboard' });
    } catch (error) {
      console.error('Error copying waiver link:', error);
      setMessage({ type: 'error', text: 'Unable to copy the waiver link' });
    }
  };

  const handleLookup = async () => {
    if (!tenantId || !lookupEmail.trim()) return;

    setLookupLoading(true);
    setLookupResult(null);
    setMessage(null);

    try {
      const emailLower = normalizeEmail(lookupEmail);
      const keySnapshot = await getDoc(
        doc(db, 'tenants', tenantId, 'waiverKeys', `email_${emailLower}`)
      );

      if (!keySnapshot.exists()) {
        setMessage({ type: 'error', text: 'No signed waiver found for that email yet' });
        return;
      }

      const keyData = keySnapshot.data() as WaiverKey;
      setLookupResult({
        emailLower,
        signedAt: keyData.latestSignedAt?.toDate?.().toLocaleString?.() || null,
        signatureId: keyData.latestSignatureId,
        memberId: keyData.memberId || null,
        leadId: keyData.leadId || null,
      });
    } catch (error: any) {
      console.error('Error looking up waiver status:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to look up waiver status' });
    } finally {
      setLookupLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Digital Waivers
          </Typography>
          <Typography color="text.secondary">
            Publish one active waiver, share it publicly, and track who has already signed.
          </Typography>
        </Box>

        {message && (
          <Alert severity={message.type} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DescriptionIcon color="primary" />
                  <Typography variant="h6">Active Waiver</Typography>
                </Stack>
                {activeTemplate ? (
                  <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Version ${activeTemplate.version}`} color="primary" />
                      <Chip label={activeTemplate.originalFileName} variant="outlined" />
                    </Stack>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {activeTemplate.title}
                      </Typography>
                      <Typography color="text.secondary">
                        Uploaded {formatTimestamp(activeTemplate.createdAt)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">
                    No active waiver has been published yet. Upload a PDF to start collecting signatures.
                  </Alert>
                )}
                {canManageTemplates && (
                  <>
                    <Divider />
                    <TextField
                      label="Waiver title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="General liability waiver"
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<FileUploadIcon />}
                      >
                        {selectedFile ? selectedFile.name : 'Choose PDF'}
                        <input hidden accept="application/pdf" type="file" onChange={handleFileChange} />
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={uploading || !selectedFile}
                      >
                        {uploading
                          ? 'Publishing...'
                          : activeTemplate
                            ? 'Replace Active Waiver'
                            : 'Publish Waiver'}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      PDF only, up to 5 MB.
                    </Typography>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
            <CardContent>
              <Stack spacing={2} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <QrCode2Icon color="primary" />
                  <Typography variant="h6">Public Link</Typography>
                </Stack>
                <QRCodeSVG value={publicWaiverUrl || 'https://fitstac.app'} size={180} />
                <TextField
                  fullWidth
                  label="Waiver URL"
                  value={publicWaiverUrl}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  disabled={!publicWaiverUrl}
                >
                  Copy Public Link
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Waiver Lookup</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  fullWidth
                  label="Email"
                  value={lookupEmail}
                  onChange={(event) => setLookupEmail(event.target.value)}
                  placeholder="member@example.com"
                />
                <Button variant="contained" onClick={handleLookup} disabled={lookupLoading || !lookupEmail.trim()}>
                  {lookupLoading ? 'Checking...' : 'Check Status'}
                </Button>
              </Stack>
              {lookupResult && (
                <Alert severity="success">
                  <Typography fontWeight={600}>{lookupResult.emailLower}</Typography>
                  <Typography variant="body2">Signed on {lookupResult.signedAt || 'Unknown date'}</Typography>
                  {lookupResult.memberId && (
                    <Typography variant="body2">Member ID: {lookupResult.memberId}</Typography>
                  )}
                  {lookupResult.leadId && (
                    <Typography variant="body2">Lead ID: {lookupResult.leadId}</Typography>
                  )}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Recent Signatures</Typography>
              {recentSignatures.length === 0 ? (
                <Typography color="text.secondary">No waiver signatures have been recorded yet.</Typography>
              ) : (
                <List disablePadding>
                  {recentSignatures.map((signature, index) => (
                    <Box key={signature.id}>
                      {index > 0 && <Divider />}
                      <ListItem disableGutters>
                        <ListItemText
                          primary={`${signature.signerFirstName} ${signature.signerLastName}`}
                          secondary={`${signature.signerEmail} • ${formatTimestamp(signature.signedAt)}`}
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
